import { Fragment } from 'react';
import Text14 from '@sdk/components/ui/typography/Text14';
import gettext from '@sdk/lang/gettext';
import Row from '@sdk/components/ui/spacing/Row';
import formatTime from '@sdk/utils/dateFormat/formatTime';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import type { TimesheetHistoryTimeEntry } from '@sdk/timesheets/types/TimesheetHistory';
import useAuth from '@sdk/authentication/useAuth';

import getBreakName from '@web/timeClock/utils/getBreakName';

type Props = {
  editedChange: TimesheetHistoryTimeEntry;
};

const TimesheetHistoryRowsForBreaksArray = ({ editedChange }: Props) => {
  const { currentUser } = useAuth();

  return (
    <>
      {editedChange.breaks.map((breakObj, index) => {
        return (
          <Fragment key={index}>
            {Boolean(breakObj.startTo && !breakObj.startFrom) && (
              <>
                <Text14 themeColor={ThemeColorType.gray} mTop={5}>
                  {gettext('Added %(breakName)s', {
                    breakName: getBreakName(breakObj.type),
                  })}
                </Text14>
                <Row>
                  <Text14 themeColor={ThemeColorType.gray}>
                    <TranslateWithComponents
                      replaceComponents={{
                        time: (
                          <Text14 themeColor={ThemeColorType.success}>
                            {formatTime(breakObj.startTo, currentUser.settings.usTimeFormat)}
                          </Text14>
                        ),
                      }}>
                      {gettext('Start [[time]]')}
                    </TranslateWithComponents>
                  </Text14>
                  {breakObj.endTo && (
                    <Text14 themeColor={ThemeColorType.gray}>
                      {'\xa0'}
                      <TranslateWithComponents
                        replaceComponents={{
                          time: (
                            <Text14 themeColor={ThemeColorType.success}>
                              {formatTime(breakObj.endTo, currentUser.settings.usTimeFormat)}
                            </Text14>
                          ),
                        }}>
                        {gettext('End [[time]]')}
                      </TranslateWithComponents>
                    </Text14>
                  )}
                </Row>
              </>
            )}
            {Boolean(breakObj.startTo && breakObj.startFrom) && (
              <>
                <Text14 themeColor={ThemeColorType.gray} mTop={5}>
                  {gettext('Edited %(breakName)s start time', {
                    breakName: getBreakName(breakObj.type),
                  })}
                </Text14>
                <Text14 themeColor={ThemeColorType.gray}>
                  <TranslateWithComponents
                    replaceComponents={{
                      fromTime: (
                        <Text14 themeColor={ThemeColorType.danger}>
                          {formatTime(breakObj.startFrom, currentUser.settings.usTimeFormat)}
                        </Text14>
                      ),
                      toTime: (
                        <Text14 themeColor={ThemeColorType.success}>
                          {formatTime(breakObj.startTo, currentUser.settings.usTimeFormat)}
                        </Text14>
                      ),
                    }}>
                    {gettext('From [[fromTime]] to [[toTime]]')}
                  </TranslateWithComponents>
                </Text14>
              </>
            )}
            {Boolean(!breakObj.startTo && breakObj.startFrom) && (
              <>
                <Text14 themeColor={ThemeColorType.gray} mTop={5}>
                  {gettext('Deleted %(breakName)s', {
                    breakName: getBreakName(breakObj.type),
                  })}
                </Text14>
                <Row>
                  <Text14 themeColor={ThemeColorType.gray}>
                    <TranslateWithComponents
                      replaceComponents={{
                        time: (
                          <Text14 themeColor={ThemeColorType.danger}>
                            {formatTime(breakObj.startFrom, currentUser.settings.usTimeFormat)}
                          </Text14>
                        ),
                      }}>
                      {gettext('Start [[time]]')}
                    </TranslateWithComponents>
                  </Text14>
                  {breakObj.endFrom && (
                    <Text14 themeColor={ThemeColorType.gray}>
                      {'\xa0'}
                      <TranslateWithComponents
                        replaceComponents={{
                          time: (
                            <Text14 themeColor={ThemeColorType.danger}>
                              {formatTime(breakObj.endFrom, currentUser.settings.usTimeFormat)}
                            </Text14>
                          ),
                        }}>
                        {gettext('End [[time]]')}
                      </TranslateWithComponents>
                    </Text14>
                  )}
                </Row>
              </>
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default TimesheetHistoryRowsForBreaksArray;
